import { useOrderAccess } from '@/hooks/queries/useOrderAccess';
import { usePublicOrders } from '@/hooks/queries/usePublicOrders';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Inbox,
  LayoutDashboard,
  LineChart,
  Package,
  ReceiptText,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { QuickActionSheet } from '@/components/mobile/QuickActionSheet';
import { CustomerWizard } from '@/pages/customers/CustomerWizard';
import { StockAdjustmentWizard } from '@/pages/stock/StockAdjustmentWizard';
import { ownerPath } from '@/lib/hosts';
import { useTimeOfDay } from '@/hooks/useTimeOfDay';
import { TIME_STATE_CONFIG } from '@/lib/timeStateConfig';

interface TabItem {
  icon: LucideIcon;
  href: string;
  labelKey: string;
  matchers: string[];
  label?: string;
  count?: number;
}

const TAB_ITEMS: TabItem[] = [
  {
    icon: LayoutDashboard,
    href: '/dashboard',
    labelKey: 'tab_dashboard',
    matchers: ['/dashboard'],
  },
  {
    icon: ReceiptText,
    href: '/sales',
    labelKey: 'tab_sales',
    matchers: ['/sales', '/sales/statistics', '/pos'],
  },
  {
    icon: Package,
    href: '/products',
    labelKey: 'tab_products',
    matchers: ['/products', '/categories', '/brands', '/attributes', '/products/import'],
  },
  {
    icon: LineChart,
    href: '/stock',
    labelKey: 'tab_stock',
    matchers: ['/stock', '/stock/movements', '/stock/alerts', '/stock/warehouses'],
  },
];

function matchesPath(pathname: string, prefixes: string[]) {
  return prefixes.some((prefix) => {
    const resolved = ownerPath(prefix);
    return (
      pathname === prefix ||
      pathname.startsWith(`${prefix}/`) ||
      pathname === resolved ||
      pathname.startsWith(`${resolved}/`)
    );
  });
}

function TabLink({ tab, t }: { tab: TabItem & { active: boolean }; t: (key: string) => string }) {
  return (
    <li className="relative">
      <Link
        to={ownerPath(tab.href)}
        className="flex h-full w-full flex-col items-center justify-center gap-1"
        aria-label={`${tab.label ?? t(tab.labelKey)}${tab.count ? `, ${tab.count} à accepter` : ''}`}
      >
        <tab.icon
          className={cn(
            'h-5 w-5 transition-colors',
            tab.active ? 'text-lime-600' : 'text-charcoal-400 dark:text-charcoal-500',
          )}
          strokeWidth={tab.active ? 2 : 1.75}
        />
        <span
          className={cn(
            'font-mono text-[9px] uppercase tracking-[0.08em] leading-none transition-colors',
            tab.active
              ? 'font-semibold text-charcoal-900 dark:text-charcoal-50'
              : 'font-medium text-charcoal-500 dark:text-charcoal-400',
          )}
        >
          {tab.label ?? t(tab.labelKey)}
        </span>
        {!!tab.count && (
          <span className="absolute right-1 top-0 rounded-full bg-lime-400 px-1.5 text-xs font-semibold text-charcoal-950">
            {tab.count > 99 ? '99+' : tab.count}
          </span>
        )}
        {tab.active && (
          <span
            aria-hidden
            className="absolute -bottom-0 left-1/2 size-1 -translate-x-1/2 rounded-full bg-lime-500"
          />
        )}
      </Link>
    </li>
  );
}

export function BottomTabBar() {
  const orderAccess = useOrderAccess();
  const { data: orderCount } = usePublicOrders(
    { workflow: 'new', per_page: 1 },
    { enabled: orderAccess },
  );
  const { pathname } = useLocation();
  const { t } = useTranslation('navigation');
  const [quickActionOpen, setQuickActionOpen] = useState(false);
  const [customerWizardOpen, setCustomerWizardOpen] = useState(false);
  const [stockWizardOpen, setStockWizardOpen] = useState(false);
  const timeOfDay = useTimeOfDay();
  const timeConfig = TIME_STATE_CONFIG[timeOfDay];
  const FabIcon = timeConfig.ctaIcon;
  const FAB_TONE_CLASSES = {
    sunrise:
      'bg-amber-500 text-white shadow-[0_8px_18px_-4px_rgba(245,158,11,0.55)] hover:bg-amber-600',
    lime: 'bg-lime-400 text-charcoal-950 shadow-[0_8px_18px_-4px_rgba(163,230,53,0.5)] hover:bg-lime-300',
    sunset:
      'bg-orange-500 text-white shadow-[0_8px_18px_-4px_rgba(249,115,22,0.55)] hover:bg-orange-600',
    night: 'bg-charcoal-700 text-white shadow-sm hover:bg-charcoal-600',
  } as const;

  const isAiChat = pathname.startsWith('/ai');
  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(0);

  // Keyboard detection via visualViewport
  useEffect(() => {
    if (!isAiChat) {
      setHidden(false);
      return;
    }

    const vv = window.visualViewport;
    if (!vv) return;

    const initialHeight = vv.height;

    function handleResize() {
      if (!vv) return;
      const keyboardOpen = initialHeight - vv.height > 150;
      if (keyboardOpen) {
        setHidden(true);
      }
    }

    vv.addEventListener('resize', handleResize);
    return () => vv.removeEventListener('resize', handleResize);
  }, [isAiChat]);

  // Scroll direction detection
  useEffect(() => {
    if (!isAiChat) return;

    function handleScroll() {
      const currentY = window.scrollY;
      if (currentY > lastScrollY.current + 10) {
        setHidden(true);
      } else if (currentY < lastScrollY.current - 10) {
        setHidden(false);
      }
      lastScrollY.current = currentY;
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isAiChat]);

  const resolvedTabs = useMemo(
    () =>
      TAB_ITEMS.map((item) =>
        item.href === '/stock' && orderAccess
          ? {
              icon: Inbox,
              href: '/commerce/orders',
              labelKey: 'commerce_orders',
              label: 'Commandes',
              matchers: ['/commerce/orders'],
              count: orderCount?.meta?.total,
            }
          : item,
      ).map((tab) => ({
        ...tab,
        active: matchesPath(pathname, tab.matchers),
      })),
    [pathname, orderAccess, orderCount?.meta?.total],
  );

  return (
    <>
      <div
        data-floating-dock
        className={cn(
          'fixed bottom-0 left-0 right-0 z-40 lg:hidden transition-transform duration-200 ease-out',
          hidden && isAiChat && 'translate-y-full',
        )}
      >
        <nav
          className="border-t border-slate-200 bg-white/95 backdrop-blur dark:border-charcoal-800 dark:bg-charcoal-900/95"
          style={{ paddingBottom: 'var(--bottom-tab-safe-area)' }}
          aria-label="Mobile tabs"
          data-tour="bottom-tabs"
        >
          <ul className="grid h-[var(--bottom-tab-height)] grid-cols-5">
            {resolvedTabs.slice(0, 2).map((tab) => (
              <TabLink key={tab.href} tab={tab} t={t} />
            ))}

            {/* FAB — time-aware (color + icon adapt to time state) */}
            <li className="relative">
              <button
                type="button"
                onClick={() => setQuickActionOpen(true)}
                className="flex h-full w-full items-center justify-center"
                aria-label={t('tab_action')}
                aria-expanded={quickActionOpen}
              >
                <span
                  className={cn(
                    '-mt-6 grid size-14 place-items-center rounded-2xl transition-[background,transform,box-shadow] active:scale-95',
                    FAB_TONE_CLASSES[timeConfig.tone],
                  )}
                >
                  <FabIcon className="size-6" strokeWidth={2.25} />
                </span>
              </button>
            </li>

            {resolvedTabs.slice(2).map((tab) => (
              <TabLink key={tab.href} tab={tab} t={t} />
            ))}
          </ul>
        </nav>
      </div>

      <QuickActionSheet
        open={quickActionOpen}
        onOpenChange={setQuickActionOpen}
        onOpenCustomerWizard={() => setCustomerWizardOpen(true)}
        onOpenStockWizard={() => setStockWizardOpen(true)}
      />
      <CustomerWizard open={customerWizardOpen} onOpenChange={setCustomerWizardOpen} />
      <StockAdjustmentWizard open={stockWizardOpen} onOpenChange={setStockWizardOpen} />
    </>
  );
}
